/**
 * Shared card-title resolver.
 *
 * Pure function — **no React imports, no Zod imports, no fetch**. It is the
 * single declarative resolution point a configurable card uses to render its
 * (otherwise hardcoded) `<Card.Header>` title, so a layout rule can rename it
 * via {@link LayoutOverrides.title} without each card hand-rolling resolution.
 *
 * Most cards wrap their host in a base `<Card>` with a hardcoded
 * `<Card.Header><Text weight="semibold">…</Text>`. Routing that title through
 * this helper turns it into a single overridable value:
 *
 * ```tsx
 * <Text weight="semibold">
 *   {resolveCardTitle('Ajouter un intervenant', _layoutOverrides)}
 * </Text>
 * ```
 *
 * The card also publishes the same base title via `usePublishLayoutConfig` so
 * the admin editor can target it. With no rule authored, `overrides` is
 * `undefined`/empty and the base title is returned unchanged — zero visible
 * change to any card. See the layout-editor free-text plan §4.4.
 */

import type { LayoutOverrides } from './layoutOverrides';

/**
 * Returns the tenant-overridden component title when present, else the base
 * title. Lets a card render its (otherwise hardcoded) `<Card.Header>` title
 * from a single declarative value a layout rule can target via
 * {@link LayoutOverrides.title}.
 *
 * @param baseTitle - The card's default (hardcoded) title.
 * @param overrides - Tenant-scoped layout overrides; `title` wins when defined.
 * @returns The override `title` when present, otherwise `baseTitle`.
 */
export function resolveCardTitle(
  baseTitle: string,
  overrides?: LayoutOverrides,
): string {
  return overrides?.title ?? baseTitle;
}
